/**
 * IndexNowKeyCard.jsx — Admin › Keys › IndexNow configuration.
 *
 * Stores the IndexNow key in app_config (DB) so the admin can rotate
 * it without a redeploy. The key is served publicly at
 * GET /api/indexnow-verify so Bing/Yandex can validate domain ownership.
 *
 * How IndexNow works:
 *   1. Generate a key (any 8–128 char hex/UUID string) at
 *      https://www.bing.com/indexnow/getstarted
 *   2. Save it here → the backend serves it at /api/indexnow-verify
 *   3. Bing pings /api/indexnow-verify to confirm you own the domain
 *   4. Then use "Sitemap Ping" below to push URL updates to Bing/Yandex
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, ExternalLink, Eye, EyeOff,
  Globe, Loader2, Save, Trash2, Copy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function IndexNowKeyCard({ cfg, onSaved }) {
  const [keyInput, setKeyInput] = useState('');
  const [show, setShow]         = useState(false);
  const [saving, setSaving]     = useState(false);
  const [copied, setCopied]     = useState(false);
  const [verified, setVerified] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const configured = Boolean(cfg?.indexnow_key_configured);
  const existingKey = cfg?.indexnow_key || '';
  const verifyUrl   = `${process.env.REACT_APP_BACKEND_URL || ''}/api/indexnow-verify`;

  const save = async () => {
    if (!keyInput.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config`, { indexnow_key: keyInput.trim() }, { withCredentials: true });
      toast.success('IndexNow key saved.');
      setKeyInput('');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save IndexNow key.');
    } finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config`, { indexnow_key: '' }, { withCredentials: true });
      toast.success('IndexNow key cleared.');
      setVerified(null);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear IndexNow key.');
    } finally { setSaving(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const r = await axios.get(verifyUrl, { withCredentials: false });
      setVerified({ ok: true, key: r.data });
    } catch (e) {
      setVerified({ ok: false, message: e?.response?.status === 404 ? 'Key not configured yet.' : 'Endpoint unreachable.' });
    } finally { setVerifying(false); }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(verifyUrl).then(() => {
      setCopied(true);
      toast.success('Verify URL copied.');
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4 space-y-3"
         data-testid="indexnow-key-card">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold">
            <Globe className="h-4 w-4 text-sky-500" />
            IndexNow Key
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Lets Bing / Yandex / Seznam instantly index new or updated URLs instead of waiting for the
            crawler.{' '}
            {configured
              ? <span className="text-emerald-700 font-medium">Key is configured and active.</span>
              : <span className="text-amber-700">Not configured — IndexNow pings will fail.</span>}
          </div>
        </div>
        <a
          href="https://www.bing.com/indexnow/getstarted"
          target="_blank"
          rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-1 text-[10.5px] text-muted-foreground hover:text-foreground"
        >
          Get key <ExternalLink className="h-2.5 w-2.5" />
        </a>
      </div>

      {/* Input row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Input
            type={show ? 'text' : 'password'}
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder={configured ? '•••••••• (enter new key to replace)' : 'e.g. 550e8400-e29b-41d4-a716-446655440000'}
            className="h-8 pr-8 font-mono text-[11.5px]"
            data-testid="indexnow-key-input"
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          />
          <button
            type="button"
            onClick={() => setShow(v => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            tabIndex={-1}
          >
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={saving || !keyInput.trim()}
          className="h-8 gap-1.5 text-[12px]"
          data-testid="indexnow-key-save"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </Button>
        {configured && (
          <Button
            size="sm"
            variant="ghost"
            onClick={clear}
            disabled={saving}
            className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
            title="Clear key"
            data-testid="indexnow-key-clear"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>

      {/* Key verification URL */}
      <div className="rounded-sm border border-dashed border-border bg-muted/30 p-3 space-y-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold">
          Key verification endpoint
        </div>
        <p className="text-[10.5px] text-muted-foreground leading-relaxed">
          Bing pings this URL to confirm you own the domain. No static file needed —
          the key is served dynamically from the database.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="flex-1 min-w-0 truncate rounded bg-muted px-2 py-1 font-mono text-[10.5px] text-foreground"
                data-testid="indexnow-verify-url">
            {verifyUrl}
          </code>
          <Button
            size="sm"
            variant="outline"
            onClick={copyUrl}
            className="h-7 gap-1 text-[11px] shrink-0"
            data-testid="indexnow-copy-url-btn"
          >
            {copied ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          {configured && (
            <Button
              size="sm"
              variant="outline"
              onClick={verify}
              disabled={verifying}
              className="h-7 gap-1 text-[11px] shrink-0"
              data-testid="indexnow-verify-btn"
            >
              {verifying ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              Test endpoint
            </Button>
          )}
        </div>
        {verified && (
          <div className={`mt-1 flex items-center gap-1.5 text-[11px] ${verified.ok ? 'text-emerald-700' : 'text-rose-600'}`}>
            {verified.ok
              ? <><CheckCircle2 className="h-3 w-3" /> Endpoint active — returns key correctly.</>
              : <><span className="font-semibold">Failed:</span> {verified.message}</>}
          </div>
        )}
      </div>

      {/* How-to steps */}
      <ol className="text-[10.5px] text-muted-foreground space-y-1 list-none border-t border-border/50 pt-2">
        <li className="flex gap-2">
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[9.5px] h-fit">1</span>
          Go to <a href="https://www.bing.com/indexnow/getstarted" target="_blank" rel="noreferrer" className="text-primary hover:underline">bing.com/indexnow/getstarted</a> → generate a key (or use any UUID).
        </li>
        <li className="flex gap-2">
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[9.5px] h-fit">2</span>
          Paste the key above and click <strong>Save</strong>.
        </li>
        <li className="flex gap-2">
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[9.5px] h-fit">3</span>
          Click <strong>Test endpoint</strong> to confirm Bing can reach the verification URL.
        </li>
        <li className="flex gap-2">
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[9.5px] h-fit">4</span>
          Use <strong>Sitemap Ping → Ping now</strong> to submit URL updates to Bing/Yandex.
        </li>
      </ol>
    </div>
  );
}
